import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { MapPin, Calendar, ArrowUpRight } from "lucide-react";
import { JsonLd } from "@/components/JsonLd";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  on_sale: { label: "On Sale", cls: "bg-white/5 text-white/70 border-white/10" },
  limited: { label: "Limited", cls: "bg-yellow-600/20 text-yellow-400 border-yellow-700/40" },
  sold_out: { label: "Sold Out", cls: "bg-red-600/20 text-red-400 border-red-700/40" },
  free: { label: "Free Show", cls: "bg-green-600/20 text-green-400 border-green-700/40" },
};

const formatDate = (iso) => {
  if (!iso) return { month: "—", day: "—" };
  const d = new Date(iso + "T12:00:00");
  return {
    month: d.toLocaleString("en-US", { month: "short" }),
    day: d.getDate().toString().padStart(2, "0"),
  };
};

export const Shows = () => {
  const settings = useSiteSettings();
  const { data: shows = [], isPending } = useQuery({
    queryKey: ["shows"],
    queryFn: async () => (await axios.get(`${API_BASE}/shows`)).data ?? [],
  });

  const eventSchema =
    shows.length > 0
      ? {
          "@context": "https://schema.org",
          "@graph": shows
            .filter((show) => show.date)
            .map((show) => ({
              "@type": "MusicEvent",
              name: `The Randy Loveless Band${show.venue ? ` at ${show.venue}` : ""}`,
              startDate: show.time ? `${show.date}T${show.time}` : show.date,
              eventStatus: "https://schema.org/EventScheduled",
              eventAttendanceMode:
                "https://schema.org/OfflineEventAttendanceMode",
              performer: { "@type": "MusicGroup", name: "The Randy Loveless Band" },
              location: {
                "@type": "Place",
                name: show.venue || "Venue TBA",
                address: show.city || undefined,
              },
              ...(show.ticket_url
                ? {
                    offers: {
                      "@type": "Offer",
                      url: show.ticket_url,
                      availability:
                        show.status === "sold_out"
                          ? "https://schema.org/SoldOut"
                          : "https://schema.org/InStock",
                    },
                  }
                : {}),
            })),
        }
      : null;

  return (
    <>
    {eventSchema && <JsonLd id="shows" data={eventSchema} />}
    <section
      id="shows"
      data-testid="shows-section"
      className="relative bg-[#0A0A0A] py-24 md:py-32 px-6 md:px-16 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex items-end justify-between mb-12 flex-wrap gap-6"
        >
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#DC2626]" />
              <span className="font-oswald text-xs uppercase tracking-[0.3em] text-[#DC2626]">
                On The Road
              </span>
            </div>
            <h2 className="font-anton uppercase text-white text-5xl md:text-6xl lg:text-7xl leading-[0.9]">
              Upcoming
              <br />
              <span className="text-[#B91C1C]">Shows</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-sm font-manrope">
            {settings.shows_note ||
              "Catch us live. New dates announced monthly — sign up below to be the first to know."}
          </p>
        </motion.div>

        {isPending ? null : shows.length === 0 ? (
          <div className="border-2 border-dashed border-white/10 p-16 text-center">
            <Calendar size={32} className="text-white/30 mx-auto mb-3" />
            <div className="font-oswald uppercase tracking-[0.2em] text-white/40 text-sm">
              New dates coming soon — sign up below
            </div>
          </div>
        ) : (
          <div className="border-t border-white/10">
            {shows.map((show, idx) => {
              const d = formatDate(show.date);
              const st = STATUS_LABELS[show.status] || STATUS_LABELS.on_sale;
              return (
                <motion.div
                  key={show.id}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  data-testid={`show-row-${show.id}`}
                  className="group border-b border-white/10 hover:bg-[#150A0A] transition-colors"
                >
                  <div className="grid grid-cols-12 items-center gap-4 px-2 md:px-6 py-6 md:py-7">
                    <div className="col-span-3 md:col-span-2 flex flex-col">
                      <span className="font-oswald uppercase tracking-[0.2em] text-xs text-[#DC2626]">
                        {d.month}
                      </span>
                      <span className="font-anton text-4xl md:text-5xl text-white leading-none">
                        {d.day}
                      </span>
                    </div>

                    <div className="col-span-9 md:col-span-5">
                      <div className="font-oswald uppercase tracking-wide text-white text-xl md:text-2xl group-hover:text-[#DC2626] transition-colors">
                        {show.venue}
                      </div>
                      <div className="flex items-center gap-2 text-white/60 text-sm mt-1">
                        <MapPin size={14} />
                        <span>{show.city}</span>
                      </div>
                    </div>

                    <div className="hidden md:flex col-span-2 items-center gap-2 text-white/70 font-oswald uppercase tracking-wider text-sm">
                      <Calendar size={14} />
                      {show.time}
                    </div>

                    <div className="col-span-12 md:col-span-3 flex md:justify-end items-center gap-3 mt-3 md:mt-0">
                      <span
                        className={`font-oswald uppercase tracking-[0.18em] text-[10px] px-2.5 py-1 border ${st.cls}`}
                      >
                        {st.label}
                      </span>
                      {show.ticket_url ? (
                        <a
                          href={show.ticket_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`show-ticket-btn-${show.id}`}
                          className="group/btn inline-flex items-center gap-2 font-oswald uppercase tracking-[0.2em] text-xs text-white border border-white/30 hover:border-[#B91C1C] hover:bg-[#B91C1C] px-4 py-2.5 transition-all"
                        >
                          Tickets
                          <ArrowUpRight
                            size={14}
                            className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform"
                          />
                        </a>
                      ) : (
                        <span className="font-oswald uppercase tracking-[0.18em] text-[10px] text-white/30">
                          Tickets soon
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
    </>
  );
};
